import { SignalDateGroup, SignalGroup } from '@/types/signals';

const getPageUrlKey = (url?: string | null) => {
  if (!url) return '';

  try {
    const value = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
    const parsed = new URL(value);
    return `${parsed.hostname.replace(/^www\./, '').toLowerCase()}${parsed.pathname.replace(
      /\/$/,
      ''
    )}${parsed.search}`.toLowerCase();
  } catch {
    return url
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '')
      .toLowerCase();
  }
};

const getSignalContactName = (signal: SignalGroup['signals'][number]) =>
  [signal.contact?.first_name, signal.contact?.last_name].filter(Boolean).join(' ');

const splitPageGroupByUrl = (group: SignalGroup): SignalGroup[] => {
  if (group.entity !== 'page' || group.type !== 'visited') {
    return [group];
  }

  const groupedSignals = group.signals.reduce<Record<string, typeof group.signals>>(
    (acc, signal) => {
      const key = getPageUrlKey(signal.page?.url) || signal.id;
      acc[key] = acc[key] || [];
      acc[key].push(signal);
      return acc;
    },
    {}
  );

  return Object.values(groupedSignals).map(signals => ({
    ...group,
    contact_id: signals[0]?.contact?.id || group.contact_id,
    contact_name: getSignalContactName(signals[0]) || group.contact_name,
    total: signals.length,
    signals,
  }));
};

const mergePageGroupsByUrl = (groups: SignalGroup[]): SignalGroup[] => {
  const mergedGroups: SignalGroup[] = [];

  groups.forEach(group => {
    if (group.entity !== 'page' || group.type !== 'visited') {
      mergedGroups.push(group);
      return;
    }

    const signal = group.signals[0];
    const contactKey = group.contact_id || signal?.contact?.id || 'no-contact';
    const targetPageKey =
      signal?.page?.target_page_id || signal?.page?.target_page_short_name || 'unmatched';
    const pageUrlKey = getPageUrlKey(signal?.page?.url);
    const key = `${contactKey}:${targetPageKey}:${pageUrlKey}`;
    const existing = mergedGroups.find(
      mergedGroup =>
        mergedGroup.entity === 'page' &&
        mergedGroup.type === 'visited' &&
        `${mergedGroup.contact_id || mergedGroup.signals[0]?.contact?.id || 'no-contact'}:${mergedGroup.signals[0]?.page?.target_page_id || mergedGroup.signals[0]?.page?.target_page_short_name || 'unmatched'}:${getPageUrlKey(
          mergedGroup.signals[0]?.page?.url
        )}` === key
    );

    if (existing) {
      existing.signals = [...existing.signals, ...group.signals];
      existing.total = existing.signals.length;
      return;
    }

    mergedGroups.push(group);
  });

  return mergedGroups;
};

export const getSignalGroups = (dateGroup: SignalDateGroup): SignalGroup[] => {
  if (dateGroup.groups?.length) {
    return mergePageGroupsByUrl(dateGroup.groups.flatMap(splitPageGroupByUrl));
  }

  const groups =
    dateGroup.target_pages?.flatMap(targetPage =>
      (targetPage.groups || [])
        .map(group => ({
          ...group,
          signals: group.signals.map(signal => ({
            ...signal,
            page: signal.page
              ? {
                  ...signal.page,
                  target_page_id: signal.page.target_page_id || targetPage.target_page_id,
                  target_page_short_name:
                    signal.page.target_page_short_name || targetPage.target_page_short_name,
                }
              : signal.page,
          })),
        }))
        .flatMap(splitPageGroupByUrl)
    ) || [];

  return mergePageGroupsByUrl(groups);
};
